import { anzahlwort, betrag, kontobezeichnung, periodenname, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Handbuchausgabe, Regelparameter } from './typen'

/**
 * Was sich zwischen zwei Ausgaben des Konzernhandbuchs geändert hat
 * (2.11 Abschnitt 9.6, R780).
 *
 * **Die Frage, die ein Prüfer stellt:** *„Was hat sich zwischen Ausgabe 1 und
 * 2 geändert?"* Die Liste der Ausgaben beantwortet sie nicht — sie sagt, was
 * jetzt gilt und was damals galt, aber nicht, was dazwischen passiert ist.
 *
 * **Gezeigt wird nur, was sich unterscheidet** (R780). Fünfzehn Zeilen, von
 * denen vierzehn „gleich" sagen, sind eine Tabelle, in der man den einen
 * Unterschied sucht — und genau der ist die Auskunft.
 *
 * **Reine Rechnung, deshalb hier und nicht im Bildschirm:** Sie läßt sich
 * prüfen, ohne etwas zu zeichnen.
 */

/** Eine Angabe, die in zwei Ausgaben verschieden lautet. */
export type Unterschied = {
  /** Die Beschriftung, wie sie auch auf dem Pflegebildschirm steht. */
  beschriftung: string
  /** Wie die Angabe in der älteren Ausgabe lautete. */
  vorher: string
  /** Wie sie in der jüngeren lautet. */
  nachher: string
}

/**
 * Die Angaben, die verglichen werden — **in der Reihenfolge des
 * Pflegebildschirms**.
 *
 * Wer einen Unterschied liest und ihn nachsehen will, findet ihn dort an
 * derselben Stelle. Die Beschriftung ist bewußt dieselbe: Zwei Wörter für
 * dasselbe Feld gehen mit der Zeit auseinander (R778).
 */
const FELDER: {
  schluessel: Sprachschluessel
  lies: (a: Handbuchausgabe) => string
}[] = [
  { schluessel: 'handbuch.feld.name', lies: (a) => a.name },
  { schluessel: 'handbuch.feld.gueltigVon', lies: (a) => periodenname(a.gueltigVon) },
  {
    schluessel: 'handbuch.feld.gueltigBis',
    lies: (a) => (a.gueltigBis === null ? t('handbuch.vergleich.offen') : periodenname(a.gueltigBis)),
  },
  // **Geschrieben wie im Konzernhandbuch** (7.2 Abschnitt 53, R1633): Bis zum
  // 2026-10-12 stand die Grenze hier als 10000 und ein Konto als Schlüssel.
  { schluessel: 'handbuch.feld.nachkommastellen', lies: (a) => anzahlwort(a.parameter.nachkommastellen) },
  { schluessel: 'handbuch.feld.icGrenze', lies: (a) => betrag(a.parameter.icDifferenzGrenze) },
  {
    // **`null` ist hier eine Aussage und keine Lücke** — „wird nicht planmäßig
    // abgeschrieben". Ein leeres Feld im Vergleich verschwiege den stärksten
    // Unterschied, den zwei Ausgaben haben können.
    schluessel: 'handbuch.feld.nutzungsdauer',
    lies: (a) =>
      a.parameter.goodwillNutzungsdauerMonate === null
        ? t('handbuch.vergleich.keineAbschreibung')
        : anzahlwort(a.parameter.goodwillNutzungsdauerMonate),
  },
  ...(
    [
      'firmenwertKonto',
      'unterschiedsbetragPassivKonto',
      'firmenwertAbschreibungKonto',
      'gewinnvortragKonto',
      'nichtBeherrschendKonto',
      'icDifferenzKonto',
      'icDifferenzKontoWaehrung',
      'zwischenergebnisKonto',
      'abschreibungFinanzanlagenKonto',
      'abgangBeteiligungenKonto',
    ] as const satisfies readonly (keyof Regelparameter)[]
  ).map((feld) => ({
    schluessel: `handbuch.feld.${feld}` as Sprachschluessel,
    lies: (a: Handbuchausgabe) => (a.parameter[feld] ? kontobezeichnung(a.parameter[feld]) : ''),
  })),
]

/**
 * Was sich von `vorher` nach `nachher` geändert hat — leer, wenn nichts.
 *
 * **Die ältere Ausgabe steht links.** Ein Vergleich, der die Richtung offen
 * ließe, wäre bei jeder zweiten Lesung falsch herum.
 */
export function unterschiede(vorher: Handbuchausgabe, nachher: Handbuchausgabe): Unterschied[] {
  return FELDER.map((f) => ({
    beschriftung: t(f.schluessel),
    vorher: f.lies(vorher),
    nachher: f.lies(nachher),
  })).filter((u) => u.vorher !== u.nachher)
}

/**
 * Die Ausgabe, gegen die verglichen wird: **die nächstältere**.
 *
 * `null` für die älteste — sie hat keine Vorgängerin, und ein Vergleich gegen
 * nichts wäre eine Tabelle, in der jede Zeile steht.
 */
export function vorgaengerin(
  alle: Handbuchausgabe[],
  ausgabe: number,
): Handbuchausgabe | null {
  return (
    alle
      .filter((a) => a.ausgabe < ausgabe)
      .sort((a, b) => b.ausgabe - a.ausgabe)[0] ?? null
  )
}
